import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useApi } from 'hooks/use-api/useApi';
import { useUpdateState } from 'hooks/useUpdateState';
import { useStateNavigation } from 'hooks/useStateNavigation';
import { useI18n } from 'utils/i18n/usei18n';
import { Constants } from 'utils/Constants';
import { PortableText } from '@portabletext/react';
import { BodyPortableText } from 'components/new-design/portable-texts/BodyPortableText';
import { SectionStationSearch } from 'components/section-station-search/SectionStationSearch';
import { IsTrainOnTimeLink } from 'elements/new-design/links/IsTrainOnTime';
import { PageTitleLarge, fontStylePride } from 'elements/new-design/Typography';
import { LargeContainer, SmallContainer } from 'components/new-design/layouts/LayoutContainers';
import { FlexColumn, FlexCenterCenter } from 'elements/containers/Containers';
import { PreviewNotAvailable } from './components/PreviewNotAvailable';
import { Loader } from 'elements/loader/Loader';

type MainPageProps = {
  preview?: boolean;
};

type StyleProps = {
  small: string;
  medium: string;
  large: string;
};

const PageContainer = styled(FlexColumn)`
  width: 100%;
  align-items: center;
`;

const MainPageTitle = styled(PageTitleLarge)`
  text-align: center;
  // TODO: Move the main page title to sanity, so go-ahead can change the font/text themselves.
  /* ${fontStylePride} */ // comment out/in as needed.
`;

const IsTrainOnTimeSection = styled.div`
  ${(_: StyleProps) => ''}
  width: ${(props) => props.small};
  padding-left: 2.4rem;

  @media ${(props) => props.theme.breakpoints.medium} {
    width: ${(props) => props.medium};
  }

  @media ${(props) => props.theme.breakpoints.large} {
    width: ${(props) => props.large};
  }
`;

const sizes = {
  small: '100%',
  medium: '36.6rem',
  large: '66rem',
};

type StartPageState = typeof Constants.LOADING | typeof Constants.IDLE | typeof Constants.ERROR;

const StartPage = ({ preview }: MainPageProps): JSX.Element => {
  const { setPageMessages, setContentLoaded } = useUpdateState();
  const { fetchMainPagePreview, fetchMainPage } = useApi().CmsApi();
  const { navigateWithState } = useStateNavigation();
  const { translate, currentLanguage } = useI18n();
  const [state, setState] = useState<StartPageState>(Constants.LOADING);
  const [startPage, setStartPage] = useState(null);
  const [pageByLanguage, setPageByLanguage] = useState(null);

  const initMainPage = async () => {
    try {
      const page = preview ? await fetchMainPagePreview({ preview }) : await fetchMainPage();
      setStartPage(page);
      setState(Constants.IDLE);
    } catch {
      setPageMessages({
        type: Constants.ERROR_MESSAGE,
        title: translate('CMS_ERROR_HEADLINE'),
        message: translate('CMS_ERROR_LOADING_MAIN_TEXT'),
        closable: true,
      });
      setState(Constants.ERROR);
    }
  };

  useEffect(() => {
    if (!preview && startPage && currentLanguage) {
      setPageByLanguage(startPage.filter((data) => data.language === currentLanguage)[0]);
    }
  }, [startPage, currentLanguage]);

  useEffect(() => {
    (async () => {
      await initMainPage();
      setContentLoaded(true);
    })();
  }, []);

  return (
    <PageContainer>
      {state === Constants.LOADING && <Loader />}
      {state === Constants.IDLE && !pageByLanguage && preview && <PreviewNotAvailable />}
      {(state === Constants.IDLE || state === Constants.ERROR) && (
        <>
          <LargeContainer>
            <MainPageTitle>
              {pageByLanguage && pageByLanguage.title ? pageByLanguage.title : translate('TRAVEL_WHERE')}
            </MainPageTitle>
          </LargeContainer>
          <SmallContainer>
            <SectionStationSearch
              small={sizes.small}
              medium={sizes.medium}
              large={sizes.large}
              displaySwap={false}
              displaySubmit={false}
              onFormValid={() => navigateWithState(Constants.ROUTE_PLANNER)}
            />
            <FlexCenterCenter>
              <IsTrainOnTimeSection small={sizes.small} medium={sizes.medium} large={sizes.large}>
                <IsTrainOnTimeLink type="link" />
              </IsTrainOnTimeSection>
            </FlexCenterCenter>
          </SmallContainer>
        </>
      )}

      {state === Constants.IDLE && pageByLanguage && (
        <>{pageByLanguage?.body ? <PortableText value={pageByLanguage.body} components={BodyPortableText} /> : null}</>
      )}
    </PageContainer>
  );
};
export default StartPage;
